import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { Title, useDataProvider, useNotify } from 'react-admin';
import { PERMISSIONS } from '../../constants';
import FeatureIntro from '../../components/FeatureIntro';
import { CouponIntegrationTestButton } from '../../components';

// ─── Permission guards ────────────────────────────────────────────────────────

const getPerms = (): string[] => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.permissions || [];
  } catch {
    return [];
  }
};

// Relatório de saúde: qualquer ADMIN vê (não depende de product-evolution).
const useCanAccess = () => getPerms().includes(PERMISSIONS.ADMIN);

// Configurar alertas: só o usuário MASTER. `admin-profile` é a permissão que libera
// o cadastro de usuários do sistema — o mesmo gate do resource `system-users`.
// O backend exige a mesma permissão no /admin/system-config (esconder o botão sem
// trancar a API seria só cosmético).
const useCanConfigureAlerts = () => getPerms().includes(PERMISSIONS.ADMIN_PROFILE);

// ─── Tipos ──────────────────────────────────────────────────────────────────────

interface IntegrationRow {
  integration: string;
  failures: number;
  avgDurationMs: number | null;
  p95DurationMs: number | null;
}
interface PlaceRow {
  placeId: string | null;
  placeName: string | null;
  chainName: string | null;
  integration: string;
  failures: number;
  avgDurationMs: number | null;
}
interface FailureRow {
  id: string;
  createdAt: string;
  integration: string;
  step: string | null;
  httpStatus: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  couponId: string | null;
  placeName: string | null;
  chainName: string | null;
  cnpj: string | null;
  giftId: string | null;
  giftProductId: string | null;
  productId: string | null;
  giftName: string | null;
  campaignName: string | null;
  requestBody: any | null;
  responseBody: any | null;
  placeId: string | null;
  /** Token que o posto tem HOJE — as falhas antigas gravaram o token redatado. */
  placeToken: string | null;
}
interface ErrorsData {
  summary: { total: number; places: number; coupons: number };
  byIntegration: IntegrationRow[];
  topFailingPlaces: PlaceRow[];
  recentFailures: FailureRow[];
}
interface Opt { id: string; name: string | null; }

interface AlertContact { name: string; phone: string; }
interface AlertOverride {
  enabled?: boolean;
  contacts?: AlertContact[];
  minFailures?: number;
  cooldownMin?: number;
}
/** Canal de saída do alerta. Vários ao mesmo tempo, global (não é por integrador). */
type AlertChannel = 'whatsapp' | 'discord';

interface AlertConfig {
  enabled: boolean;
  /** Canais ativos. Lista vazia = configurado, mas nada sai. */
  channels: AlertChannel[];
  /** Só usado no canal whatsapp. */
  templateName: string;
  /** Um envio por webhook — a URL já é o destino e o segredo. */
  discordWebhookUrls: string[];
  contacts: AlertContact[];
  minFailures: number;
  cooldownMin: number;
  overrides: Record<string, AlertOverride>;
}

const ALERT_CONFIG_KEY = 'coupon_integration_alert';
const SYSTEM_CONFIG_RESOURCE = 'admin/system-config';

/** Mesmo teto do backend (`couponIntegrationAlert/config.ts`); quem recusa de fato é o Joi. */
const MAX_DISCORD_WEBHOOKS = 5;

const EMPTY_ALERT_CONFIG: AlertConfig = {
  enabled: false,
  channels: ['whatsapp'],
  templateName: '',
  discordWebhookUrls: [],
  contacts: [],
  minFailures: 3,
  cooldownMin: 60,
  overrides: {},
};

const INTEGRATION_LABELS: Record<string, string> = {
  rti: 'RTI',
  evo: 'EVO',
  trevo: 'Trevo',
  teixeiraPinto: 'Teixeira Pinto',
  sanvitron: 'Sanvitron',
};

const COLORS = { success: '#16a34a', warning: '#d97706', danger: '#dc2626', slate: '#64748b' };

const fmtMs = (ms: number | null) => (ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);
const fmtDate = (iso: string) => new Date(iso).toLocaleString('pt-BR');

// ─── Componente ───────────────────────────────────────────────────────────────

const CouponIntegrationHealth: React.FC = () => {
  const canAccess = useCanAccess();
  const canConfigureAlerts = useCanConfigureAlerts();
  const dataProvider = useDataProvider();

  // range: presets em dias (1/7/30) ou 'custom' = intervalo específico (de/até)
  const [range, setRange] = useState<number | 'custom'>(7);
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [integration, setIntegration] = useState<string>('');
  const [chainId, setChainId] = useState<string>('');
  const [placeId, setPlaceId] = useState<string>('');
  const [chains, setChains] = useState<Opt[]>([]);
  const [places, setPlaces] = useState<Opt[]>([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [data, setData] = useState<ErrorsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<FailureRow | null>(null);
  const [configOpen, setConfigOpen] = useState(false);

  const fetchData = useCallback(async () => {
    let from: string;
    let to: string;
    if (range === 'custom') {
      // só busca quando os dois extremos do intervalo estão preenchidos
      if (!customFrom || !customTo) { setLoading(false); return; }
      from = new Date(customFrom).toISOString();
      to = new Date(customTo).toISOString();
    } else {
      from = new Date(Date.now() - range * 86400000).toISOString();
      to = new Date().toISOString();
    }
    setLoading(true);
    try {
      const filter: any = { from, to };
      if (integration) filter.integration = integration;
      if (chainId) filter.chainId = chainId;
      if (placeId) filter.placeId = placeId;
      const result = await dataProvider.getList('admin/metrics/coupon-integration-health', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter,
      });
      setData((result.data?.[0] as unknown as ErrorsData) ?? null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, range, customFrom, customTo, integration, chainId, placeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Carrega todas as redes uma vez (para o filtro com busca).
  useEffect(() => {
    dataProvider.getList('chains', {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    })
      .then((res) => setChains(res.data as unknown as Opt[]))
      .catch(() => setChains([]));
  }, [dataProvider]);

  // Postos cascateiam pela rede selecionada (station.id === place.id).
  useEffect(() => {
    if (!chainId) { setPlaces([]); return; }
    setPlacesLoading(true);
    dataProvider.getList('stations', {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: { chainId },
    })
      .then((res) => setPlaces(res.data as unknown as Opt[]))
      .catch(() => setPlaces([]))
      .finally(() => setPlacesLoading(false));
  }, [dataProvider, chainId]);

  if (!canAccess) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" variant="h6">Acesso restrito a administradores.</Typography>
      </Box>
    );
  }

  const summary = data?.summary;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Title title="Erros das Integrações de Cupom" />

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
              Erros das Integrações de Cupom
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate, mb: 2 }}>
              Falhas na geração de código no resgate (RTI / EVO / Trevo / Teixeira Pinto / Sanvitron) — para recuperar e resolver. Apenas erros são registrados.
            </Typography>
          </Box>

          {canConfigureAlerts && (
            <FeatureIntro
              id="coupon-alert-config"
              title="Alertas de falha por WhatsApp"
              description="Configure quem recebe um aviso no WhatsApp quando uma integração de cupom começar a falhar. Dá para definir contatos por integrador e desligar os envios a qualquer momento."
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => setConfigOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Configurar alertas
              </Button>
            </FeatureIntro>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <ToggleButtonGroup
            value={range}
            exclusive
            size="small"
            onChange={(_, v) => v && setRange(v)}
            sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, p: 0.5 }}
          >
            <ToggleButton value={1} sx={{ px: 2, fontWeight: 600 }}>24h</ToggleButton>
            <ToggleButton value={7} sx={{ px: 2, fontWeight: 600 }}>7 dias</ToggleButton>
            <ToggleButton value={30} sx={{ px: 2, fontWeight: 600 }}>30 dias</ToggleButton>
            <ToggleButton value="custom" sx={{ px: 2, fontWeight: 600 }}>Período</ToggleButton>
          </ToggleButtonGroup>

          {range === 'custom' && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                type="datetime-local"
                label="De"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: '#fff' }}
              />
              <TextField
                size="small"
                type="datetime-local"
                label="Até"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: '#fff' }}
              />
            </Box>
          )}

          <Select
            size="small"
            value={integration}
            displayEmpty
            onChange={(e) => setIntegration(e.target.value)}
            sx={{ minWidth: 180, bgcolor: '#fff' }}
          >
            <MenuItem value="">Todas as integrações</MenuItem>
            {Object.entries(INTEGRATION_LABELS).map(([k, label]) => (
              <MenuItem key={k} value={k}>{label}</MenuItem>
            ))}
          </Select>

          <Autocomplete
            size="small"
            options={chains}
            getOptionLabel={(o) => o.name || o.id}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={chains.find((c) => c.id === chainId) || null}
            onChange={(_, v) => { setChainId(v?.id || ''); setPlaceId(''); }}
            sx={{ width: 220, bgcolor: '#fff' }}
            renderInput={(p) => <TextField {...p} label="Rede" placeholder="Todas as redes" />}
          />

          <Autocomplete
            size="small"
            options={places}
            loading={placesLoading}
            disabled={!chainId}
            getOptionLabel={(o) => o.name || o.id}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={places.find((p) => p.id === placeId) || null}
            onChange={(_, v) => setPlaceId(v?.id || '')}
            sx={{ width: 220, bgcolor: '#fff' }}
            renderInput={(p) => (
              <TextField {...p} label="Posto" placeholder={chainId ? 'Todos os postos' : 'Selecione a rede'} />
            )}
          />
        </Box>
        {range === 'custom' && (!customFrom || !customTo) && (
          <Typography variant="caption" sx={{ color: COLORS.warning, mt: 1, display: 'block' }}>
            Selecione o início e o fim do período para carregar os dados.
          </Typography>
        )}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && summary && (
        <>
          {/* Cards de resumo */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
            <SummaryCard label="Falhas no período" value={summary.total} color={summary.total > 0 ? COLORS.danger : COLORS.slate} />
            <SummaryCard label="Postos afetados" value={summary.places} />
            <SummaryCard label="Cupons afetados" value={summary.coupons} />
          </Box>

          {/* Por integração */}
          <SectionTitle>Falhas por integração</SectionTitle>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Integração</TableCell>
                  <TableCell align="right">Falhas</TableCell>
                  <TableCell align="right">Latência média</TableCell>
                  <TableCell align="right">Latência p95</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.byIntegration ?? []).map((r) => (
                  <TableRow key={r.integration}>
                    <TableCell><strong>{INTEGRATION_LABELS[r.integration] || r.integration}</strong></TableCell>
                    <TableCell align="right">
                      <Chip size="small" label={r.failures} sx={{ bgcolor: COLORS.danger, color: '#fff', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell align="right">{fmtMs(r.avgDurationMs)}</TableCell>
                    <TableCell align="right">{fmtMs(r.p95DurationMs)}</TableCell>
                  </TableRow>
                ))}
                {!(data?.byIntegration ?? []).length && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ color: COLORS.slate }}>Nenhuma falha no período. 🎉</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Postos que mais falham */}
          <SectionTitle>Postos que mais falham</SectionTitle>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Posto</TableCell>
                  <TableCell>Rede</TableCell>
                  <TableCell>Integração</TableCell>
                  <TableCell align="right">Falhas</TableCell>
                  <TableCell align="right">Latência média</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.topFailingPlaces ?? []).map((r, i) => (
                  <TableRow key={`${r.placeId}-${r.integration}-${i}`}>
                    <TableCell>{r.placeName || '—'}</TableCell>
                    <TableCell>{r.chainName || '—'}</TableCell>
                    <TableCell>{INTEGRATION_LABELS[r.integration] || r.integration}</TableCell>
                    <TableCell align="right" sx={{ color: COLORS.danger, fontWeight: 700 }}>{r.failures}</TableCell>
                    <TableCell align="right">{fmtMs(r.avgDurationMs)}</TableCell>
                  </TableRow>
                ))}
                {!(data?.topFailingPlaces ?? []).length && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ color: COLORS.slate }}>Nenhuma falha no período. 🎉</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Falhas recentes */}
          <SectionTitle>Falhas recentes</SectionTitle>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quando</TableCell>
                  <TableCell>Integração</TableCell>
                  <TableCell>Posto / Rede</TableCell>
                  <TableCell>Brinde</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell align="right">Latência</TableCell>
                  <TableCell>Cupom</TableCell>
                  <TableCell>Payload</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.recentFailures ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</TableCell>
                    <TableCell>
                      {INTEGRATION_LABELS[r.integration] || r.integration}
                      {r.step ? <Typography component="span" variant="caption" sx={{ color: COLORS.slate }}> ({r.step})</Typography> : null}
                    </TableCell>
                    <TableCell>
                      <div>{r.placeName || '—'}</div>
                      <Typography variant="caption" sx={{ color: COLORS.slate }}>{r.chainName || ''}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Tooltip title={r.giftProductId || r.productId || r.giftId || ''}>
                        <div>
                          <Typography variant="body2" noWrap>{r.giftName || '—'}</Typography>
                          {r.campaignName ? <Typography variant="caption" sx={{ color: COLORS.slate }} noWrap display="block">{r.campaignName}</Typography> : null}
                        </div>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{r.httpStatus ?? r.errorCode ?? '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Tooltip title={r.errorMessage || ''}>
                        <Typography variant="body2" noWrap>{r.errorMessage || '—'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{fmtMs(r.durationMs)}</TableCell>
                    <TableCell>
                      <Tooltip title={r.couponId || ''}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.couponId ? `${r.couponId.slice(0, 8)}…` : '—'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {(r.requestBody || r.responseBody) ? (
                        <Button size="small" variant="text" onClick={() => setDetail(r)} sx={{ textTransform: 'none' }}>ver</Button>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {!(data?.recentFailures ?? []).length && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ color: COLORS.slate }}>Nenhuma falha no período. 🎉</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!loading && !summary && (
        <Typography sx={{ color: COLORS.slate }}>Não foi possível carregar os dados.</Typography>
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Payload — {detail ? (INTEGRATION_LABELS[detail.integration] || detail.integration) : ''}
          {detail?.step ? ` (${detail.step})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            {detail?.giftName ? <IdRow label="Brinde" value={detail.giftName} /> : null}
            {detail?.campaignName ? <IdRow label="Campanha (gift)" value={detail.campaignName} /> : null}
            {detail?.giftProductId ? <IdRow label="giftProductId" value={detail.giftProductId} /> : null}
            {detail?.productId ? <IdRow label="productId" value={detail.productId} /> : null}
            {detail?.giftId ? <IdRow label="giftId" value={detail.giftId} /> : null}
            {detail?.couponId ? <IdRow label="couponId" value={detail.couponId} /> : null}
            {/* Falhas gravadas antes de 2026-08 têm token redatado no payload: o token
                atual do posto é o que torna "token invalido" diagnosticável nelas. */}
            {detail?.placeToken ? <IdRow label="Token atual do posto" value={detail.placeToken} /> : null}
          </Box>
          <PayloadBlock label="Enviado (request)" value={detail?.requestBody} />
          <PayloadBlock label="Recebido (response)" value={detail?.responseBody} />
        </DialogContent>
        <DialogActions>
          <CouponIntegrationTestButton
            logId={detail?.id}
            placeId={detail?.placeId}
            productId={detail?.productId}
            giftProductId={detail?.giftProductId}
            integration={detail?.integration}
          />
          <Button onClick={() => setDetail(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {canConfigureAlerts && <AlertConfigDialog open={configOpen} onClose={() => setConfigOpen(false)} />}
    </Box>
  );
};

// ─── Dialog de configuração dos alertas ────────────────────────────────────────

/** Só dígitos — o backend exige DDI+DDD+número (12 ou 13 dígitos). */
const digitsOnly = (v: string) => v.replace(/\D+/g, '');

const ContactList: React.FC<{
  contacts: AlertContact[];
  onChange: (contacts: AlertContact[]) => void;
  emptyHint?: string;
}> = ({ contacts, onChange, emptyHint }) => (
  <Box>
    {contacts.map((c, i) => {
      const phoneInvalid = !!c.phone && (c.phone.length < 12 || c.phone.length > 13);
      return (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
          <TextField
            size="small"
            label="Nome"
            value={c.name}
            onChange={(e) => onChange(contacts.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label="WhatsApp"
            placeholder="5511999999999"
            value={c.phone}
            onChange={(e) => onChange(contacts.map((x, j) => (j === i ? { ...x, phone: digitsOnly(e.target.value) } : x)))}
            error={phoneInvalid}
            helperText={phoneInvalid ? 'Use DDI + DDD + número (só dígitos)' : undefined}
            inputProps={{ inputMode: 'numeric', maxLength: 13 }}
            sx={{ flex: 1 }}
          />
          <IconButton
            size="small"
            onClick={() => onChange(contacts.filter((_, j) => j !== i))}
            aria-label="Remover contato"
            sx={{ mt: 0.5 }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    })}
    {!contacts.length && emptyHint && (
      <Typography variant="caption" sx={{ color: COLORS.slate, display: 'block', mb: 1 }}>{emptyHint}</Typography>
    )}
    <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...contacts, { name: '', phone: '' }])} sx={{ textTransform: 'none' }}>
      Adicionar contato
    </Button>
  </Box>
);

/** Webhooks do Discord. Cada URL recebe uma cópia do mesmo aviso. */
const WebhookList: React.FC<{
  urls: string[];
  onChange: (urls: string[]) => void;
}> = ({ urls, onChange }) => (
  <Box>
    {urls.map((url, i) => {
      // O backend exige https: a URL carrega o segredo do webhook.
      const invalid = !!url.trim() && !url.trim().startsWith('https://');
      return (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
          <TextField
            size="small"
            label={`Webhook ${i + 1}`}
            placeholder="https://discord.com/api/webhooks/..."
            value={url}
            onChange={(e) => onChange(urls.map((x, j) => (j === i ? e.target.value : x)))}
            error={invalid}
            helperText={invalid ? 'A URL precisa começar com https://' : undefined}
            sx={{ flex: 1 }}
          />
          <IconButton
            size="small"
            onClick={() => onChange(urls.filter((_, j) => j !== i))}
            aria-label="Remover webhook"
            sx={{ mt: 0.5 }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    })}
    {!urls.length && (
      <Typography variant="caption" sx={{ color: COLORS.slate, display: 'block', mb: 1 }}>
        Nenhum webhook — o canal Discord não envia nada.
      </Typography>
    )}
    <Button
      size="small"
      startIcon={<AddIcon />}
      disabled={urls.length >= MAX_DISCORD_WEBHOOKS}
      onClick={() => onChange([...urls, ''])}
      sx={{ textTransform: 'none' }}
    >
      Adicionar webhook
    </Button>
  </Box>
);

const AlertConfigDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [config, setConfig] = useState<AlertConfig>(EMPTY_ALERT_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    dataProvider.getOne(SYSTEM_CONFIG_RESOURCE, { id: ALERT_CONFIG_KEY })
      .then((res) => {
        const { id, ...rest } = (res.data ?? {}) as any;
        // Fold do formato singular anterior ao multicanal. O backend já normaliza no
        // GET; isto aqui só cobre a janela em que o admin sobe antes dele. Sem isso a
        // tela abriria com os campos vazios e salvar apagaria a config.
        setConfig({
          ...EMPTY_ALERT_CONFIG,
          ...rest,
          channels: Array.isArray(rest.channels) ? rest.channels
            : rest.channel ? [rest.channel] : EMPTY_ALERT_CONFIG.channels,
          discordWebhookUrls: Array.isArray(rest.discordWebhookUrls) ? rest.discordWebhookUrls
            : rest.discordWebhookUrl ? [rest.discordWebhookUrl] : [],
          overrides: rest.overrides ?? {},
        });
      })
      .catch(() => notify('Não foi possível carregar a configuração de alertas', { type: 'error' }))
      .finally(() => setLoading(false));
  }, [open, dataProvider, notify]);

  const patch = (fields: Partial<AlertConfig>) => setConfig((c) => ({ ...c, ...fields }));

  const patchOverride = (integration: string, fields: AlertOverride) =>
    setConfig((c) => ({
      ...c,
      overrides: { ...c.overrides, [integration]: { ...c.overrides[integration], ...fields } },
    }));

  /** Descarta contatos incompletos e overrides que não sobrescrevem nada. */
  const buildPayload = (): AlertConfig => {
    const cleanContacts = (list?: AlertContact[]) =>
      (list ?? []).map((c) => ({ name: c.name.trim(), phone: digitsOnly(c.phone) }))
        .filter((c) => c.name && c.phone.length >= 12 && c.phone.length <= 13);

    const overrides: Record<string, AlertOverride> = {};
    for (const [integration, ov] of Object.entries(config.overrides ?? {})) {
      const next: AlertOverride = {};
      if (ov.enabled === false) next.enabled = false;
      const contacts = cleanContacts(ov.contacts);
      if (contacts.length) next.contacts = contacts;
      if (ov.minFailures) next.minFailures = Number(ov.minFailures);
      if (ov.cooldownMin) next.cooldownMin = Number(ov.cooldownMin);
      if (Object.keys(next).length) overrides[integration] = next;
    }

    return {
      enabled: config.enabled,
      channels: config.channels,
      templateName: config.templateName.trim(),
      // Dedup aqui também, e não só no Joi: URL repetida viraria aviso duplicado, e o
      // 400 do backend não diz qual das duas tirar.
      discordWebhookUrls: Array.from(
        new Set(config.discordWebhookUrls.map((url) => url.trim()).filter(Boolean)),
      ),
      contacts: cleanContacts(config.contacts),
      minFailures: Number(config.minFailures) || 1,
      cooldownMin: Number(config.cooldownMin) || 1,
      overrides,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    // O que falta para poder ligar depende de cada canal escolhido: o Discord manda nos
    // webhooks, então não depende de template aprovado nem de lista de contatos.
    if (payload.enabled) {
      if (!payload.channels.length) {
        notify('Escolha ao menos um canal para ativar os envios.', { type: 'warning' });
        return;
      }
      if (payload.channels.includes('discord') && !payload.discordWebhookUrls.length) {
        notify('Informe ao menos um webhook do Discord para ativar esse canal.', { type: 'warning' });
        return;
      }
      if (payload.channels.includes('whatsapp')) {
        if (!payload.templateName) {
          notify('Preencha o nome do template da Meta para ativar os envios.', { type: 'warning' });
          return;
        }
        if (!payload.contacts.length) {
          notify('Adicione ao menos um contato padrão para ativar os envios.', { type: 'warning' });
          return;
        }
      }
    }
    setSaving(true);
    try {
      // previousData vazio de propósito: o dataProvider faz dirty-diff por chave e
      // enviaria só os campos alterados — o backend valida o payload inteiro.
      await dataProvider.update(SYSTEM_CONFIG_RESOURCE, {
        id: ALERT_CONFIG_KEY,
        data: payload,
        previousData: {} as any,
      });
      notify('Configuração de alertas salva', { type: 'success' });
      onClose();
    } catch (e: any) {
      notify(e?.message || 'Erro ao salvar a configuração', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const showDiscord = config.channels.includes('discord');
  const showWhatsapp = config.channels.includes('whatsapp');

  const toggleChannel = (channel: AlertChannel, on: boolean) =>
    patch({
      channels: on
        ? Array.from(new Set([...config.channels, channel]))
        : config.channels.filter((c) => c !== channel),
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Alertas de falha das integrações</DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <FormControlLabel
          control={<Switch checked={config.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />}
          label="Enviar alertas"
        />
        <Typography variant="caption" sx={{ color: COLORS.slate, display: 'block', mb: 2 }}>
          Desligado, o worker continua rodando mas nenhuma mensagem é enviada.
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Canais</Typography>
        <Typography variant="caption" sx={{ color: COLORS.slate, display: 'block' }}>
          Pode ligar os dois: cada canal recebe uma cópia do mesmo aviso. Vale para todos os integradores.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={showWhatsapp} onChange={(e) => toggleChannel('whatsapp', e.target.checked)} />}
            label="WhatsApp"
          />
          <FormControlLabel
            control={<Switch checked={showDiscord} onChange={(e) => toggleChannel('discord', e.target.checked)} />}
            label="Discord"
          />
        </Box>

        {/* Blocos independentes, não exclusivos: com os dois canais ligados aparecem juntos. */}
        {showDiscord && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Webhooks do Discord</Typography>
            <Typography variant="caption" sx={{ color: COLORS.slate, display: 'block', mb: 1 }}>
              A URL já é o destino e o segredo — não precisa de template nem de contatos. Cada webhook recebe uma cópia.
            </Typography>
            <WebhookList
              urls={config.discordWebhookUrls}
              onChange={(discordWebhookUrls) => patch({ discordWebhookUrls })}
            />
          </Box>
        )}

        {showWhatsapp && (
          <TextField
            fullWidth
            size="small"
            label="Nome do template (Meta)"
            value={config.templateName}
            onChange={(e) => patch({ templateName: e.target.value })}
            helperText="Preencha após aprovar o template na Meta. Vazio = nenhum alerta é enviado."
            sx={{ mb: 2 }}
          />
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Falhas mínimas"
            value={config.minFailures}
            onChange={(e) => patch({ minFailures: Number(e.target.value) })}
            helperText="Só alerta a partir de N falhas pendentes"
            inputProps={{ min: 1 }}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="number"
            label="Cooldown (min)"
            value={config.cooldownMin}
            onChange={(e) => patch({ cooldownMin: Number(e.target.value) })}
            helperText="Silêncio após avisar"
            inputProps={{ min: 1 }}
            sx={{ flex: 1 }}
          />
        </Box>

        {showWhatsapp && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Contatos padrão</Typography>
            <ContactList
              contacts={config.contacts}
              onChange={(contacts) => patch({ contacts })}
              emptyHint="Nenhum contato — nenhum alerta será enviado."
            />
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Por integrador (opcional)</Typography>
        <Typography variant="caption" sx={{ color: COLORS.slate, display: 'block', mb: 1 }}>
          Campo em branco herda o padrão acima.
        </Typography>

        {Object.entries(INTEGRATION_LABELS).map(([key, label]) => {
          const ov = config.overrides[key] ?? {};
          return (
            <Accordion key={key} disableGutters sx={{ mb: 0.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                {ov.enabled === false && (
                  <Chip size="small" label="desligado" sx={{ ml: 1, bgcolor: COLORS.warning, color: '#fff' }} />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ov.enabled !== false}
                      onChange={(e) => patchOverride(key, { enabled: e.target.checked ? undefined : false })}
                    />
                  }
                  label="Alertar sobre esta integração"
                />
                <Box sx={{ display: 'flex', gap: 2, my: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Falhas mínimas"
                    value={ov.minFailures ?? ''}
                    onChange={(e) => patchOverride(key, { minFailures: e.target.value ? Number(e.target.value) : undefined })}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Cooldown (min)"
                    value={ov.cooldownMin ?? ''}
                    onChange={(e) => patchOverride(key, { cooldownMin: e.target.value ? Number(e.target.value) : undefined })}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                  />
                </Box>
                {showWhatsapp && (
                  <ContactList
                    contacts={ov.contacts ?? []}
                    onChange={(contacts) => patchOverride(key, { contacts })}
                    emptyHint="Sem contatos próprios — usa os contatos padrão."
                  />
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading} sx={{ textTransform: 'none' }}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const IdRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 0.5 }}>
    <Typography variant="caption" sx={{ color: COLORS.slate, fontWeight: 700, minWidth: 110 }}>{label}</Typography>
    <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{value}</Typography>
  </Box>
);

const PayloadBlock: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" sx={{ color: COLORS.slate, fontWeight: 700 }}>{label}</Typography>
    <Box
      component="pre"
      sx={{
        m: 0, mt: 0.5, p: 1.5, bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 1,
        fontSize: 12, overflow: 'auto', maxHeight: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}
    >
      {value == null ? '—' : JSON.stringify(value, null, 2)}
    </Box>
  </Box>
);

const SummaryCard: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <Card sx={{ borderRadius: 2 }}>
    <CardContent>
      <Typography variant="caption" sx={{ color: COLORS.slate, fontWeight: 600 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, color: color || '#1e293b' }}>{value}</Typography>
    </CardContent>
  </Card>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>{children}</Typography>
);

export default CouponIntegrationHealth;
